import React, { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, useWindowDimensions, View, type ViewProps } from 'react-native';

import { useSelector } from '@xstate5/react';

import { useA11ySelector } from '@sg/a11y-helpers';
import { type AddressType, Chip, theme } from '@sg/garnish';

import { useLocationSearchFlags } from '../../hooks/useLocationSearchFlags';
import { useLocationSearchHelpers } from '../../hooks/useLocationSearchHelpers';
import { type LocationSearchService } from '../../machine';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationSearchTypePicker = memo((props: LocationSearchTypePickerProps) => {
  const { locationSearchActorRef } = props;

  const { formatMessage } = useIntl();
  const { width } = useWindowDimensions();
  const a11ySelector = useA11ySelector('locations.order-channel-picker.container'); // prettier-ignore

  const locationSearchType = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.locationSearchType ?? '',
  );

  const { isLoadingLocations } = useLocationSearchFlags({ locationSearchActorRef });
  const { setLocationSearchType } = useLocationSearchHelpers({ locationSearchActorRef });

  // ─── Flags ───────────────────────────────────────────────────────────

  const isSmallScreen = width <= 375;

  // ─── Styles ──────────────────────────────────────────────────────────

  const chipStyles = [styles.chipReset, isSmallScreen ? styles.chipCompact : undefined];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View {...a11ySelector.props}>
      <Chip.Menu value={locationSearchType} onChange={setLocationSearchType} style={styles.menu}>
        {LocationSearchTypes.map((addressType) => {
          const isChecked = addressType === locationSearchType;

          return (
            <Chip
              value={addressType}
              key={addressType}
              disabled={isLoadingLocations}
              accessibilityLabel={formatMessage(messages.locationSearchAddressType, {
                addressType,
              })}
              accessibilityState={{ checked: isChecked }}
              maxFontSizeMultiplier={1.2}
              palette="primary"
              testID={`search-bar.chip.${addressType}`}
              style={chipStyles}
            >
              {formatMessage(messages.locationSearchAddressType, {
                addressType,
              })}
            </Chip>
          );
        })}
      </Chip.Menu>
    </View>
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly locationSearchAddressType: { readonly addressType: string };
}>({
  locationSearchAddressType: {
    defaultMessage: `{addressType, select,
    pickup {Pickup}
    outpost {Outpost}
    delivery {Delivery}
    other {Pickup}
  }`,
    description: 'Location Search | Address Type',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  menu: {
    justifyContent: 'center',
  },
  chipReset: {
    flex: undefined,
  },
  chipCompact: {
    paddingHorizontal: theme.spacing['3'],
  },
});

// ─────────────────────────────────────────────────────────────────────────────

const LocationSearchTypes: readonly AddressType[] = ['pickup', 'delivery', 'outpost'];

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationSearchTypePickerProps = {
  locationSearchActorRef: LocationSearchService;
} & Pick<ViewProps, 'testID'>;
